import type { DirectDealsListFilters } from '@openx/components-direct-deals/types';
import type { FilterParam } from '@openx/types/gqlTypes';
import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { getCountryNames } from './getCountryNames';

type Filters = (keyof DirectDealsListFilters)[];

const FILTER_TO_QUERY_MAP: Partial<Record<keyof DirectDealsListFilters, string>> = {
  unique_visitors: 'mln_unique_visitors',
  impressions: 'mln_impressions',
  country: 'top_countries',
} as const;

export const getQueryListFilter = (criteria: AllTableCriteria<DirectDealsListFilters>): FilterParam[] => {
  const { filters, sort } = criteria;

  if (!filters && !sort) return [];

  const filterParams: FilterParam[] = [];

  /*
    Tabs
  */
  if (filters?.tab && filters.tab !== 'all') {
    filterParams.push({
      key: filters.tab,
      value: 'true',
    });
  }

  /*
    Search filters
  */
  if (filters?.phrase) {
    filterParams.push({
      key: 'name__contains',
      value: filters.phrase,
    });
  }

  /*
    Multi-value filters
  */
  const multiValueFilters: Filters = [
    'formats',
    'devices',
    'channels',
    'geo_region',
    'ctv_type',
    'deal_types',
    'sensitive_categories',
    'country',
  ];

  multiValueFilters.forEach(filter => {
    if (filters?.[filter]) {
      const filterName = FILTER_TO_QUERY_MAP[filter] || filter;
      const filterValues = filters[filter] as string[];

      filterParams.push({
        key: `${filterName}__has_all`,
        value: (filter === 'country' ? getCountryNames(filterValues) : filterValues).join(','),
      });
    }
  });

  /*
    Range filters
  */
  const rangeFilters: Filters = ['unique_visitors', 'impressions', 'cpm_range'];

  rangeFilters.forEach(filter => {
    if (filters?.[filter]) {
      const filterName = FILTER_TO_QUERY_MAP[filter] || filter;
      const [min, max] = filters[filter];

      if (filter === 'cpm_range') {
        if (min) {
          filterParams.push({
            key: 'cpm_low__gte',
            value: min,
          });
        }
        if (max) {
          filterParams.push({
            key: 'cpm_high__lte',
            value: max,
          });
        }

        return;
      }

      if (min) {
        filterParams.push({
          key: `${filterName}__gte`,
          value: min,
        });
      }
      if (max) {
        filterParams.push({
          key: `${filterName}__lte`,
          value: max,
        });
      }
    }
  });

  /*
    Advanced filters
  */
  if (filters?.audience_distribution) {
    // ex: female;gt;0.5
    const [gender, comparison, value] = filters.audience_distribution.split(';');

    filterParams.push({
      key: `audience_distribution_${gender}__${comparison}e`,
      value,
    });
  }

  return filterParams;
};
